import React from 'react';
import { lineReconcile, sumRatedRate, splitJurisdictionLines } from '../reports/taxRate';

// Reusable jurisdiction-rate breakdown for a tax bill. Reads the rate lines already stored on the bill
// (tax_bills.jurisdiction_rates / editedFields.jurisdictions — array of {name, tax_rate, taxable_value, amount})
// and the DETECTED unit ('mills' | 'percent'). Self-styling from darkMode; not coupled to any screen — drop it
// into the TS validation screen, the CS approval/bill-detail view, or any filed-bill view.
//
// Per-line check: expected = tax_rate/1000 × taxable_value (mills) or tax_rate/100 × taxable_value (percent).
//   |expected − amount| within tolerance → ✓; otherwise ⚠ with the expected figure (points at the odd line).
//   A line with 0 rate & 0 amount, or 0 taxable value (e.g. FOREST), is neutral (—) — not flagged.

const num = (v) => { const n = parseFloat(String(v ?? '').replace(/[$,%\s]/g, '')); return isNaN(n) ? null : n; };

const money = (n) => n == null ? '—' : '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const rateStr = (n) => n == null ? '—' : Number(n).toLocaleString('en-US', { maximumFractionDigits: 6 });

// `onChange(index, field, value)` turns the table into an EDITOR. Omit it and this renders exactly as before:
// the Validate step passes it, the CS review panel and the parcel card do not, so those stay read-only.
// `onRemove(index)` adds a ✕ per row (the Validate step only) — a misread or subtotal row can be taken out.
export default function JurisdictionBreakdown({ rates, unit = 'percent', darkMode, onChange = null, onRemove = null }) {
  const editable = typeof onChange === 'function';
  const removable = typeof onRemove === 'function';
  const c = darkMode
    ? { border: '#374060', text: '#e8eaf2', muted: 'var(--slate)', sub: '#a8b0cc', th: 'rgba(26,34,56,0.7)', foot: 'rgba(0,0,0,0.2)', line: 'rgba(67,76,104,0.25)' }
    : { border: 'var(--line)', text: 'var(--ink)', muted: 'var(--slate)', sub: '#4a5068', th: '#f3f2f0', foot: '#faf9f7', line: '#eef0f6' };
  const list = Array.isArray(rates) ? rates : [];
  const divisor = unit === 'mills' ? 1000 : 100;          // fraction: mills/1000, percent/100
  const unitLabel = unit === 'mills' ? 'Rate (mills)' : 'Rate (%)';
  // Authorities taxing DIFFERENT bases (e.g. FL county $6.44M vs school $6.87M after exemptions) can't be
  // reconciled with one unit against one base — the per-line check + a single consolidated rate would be wrong
  // (factor-of-10 / mixed ✓⚠). Honest-blank the reconciliation there, same call we made on the derived rate;
  // show the transcribed lines only. Same-base multi-jurisdiction bills keep their valid reconciliation.
  const bases = list.map(r => num(r.taxable_value)).filter(v => v != null && v !== 0);
  const mixedBase = new Set(bases).size > 1;
  const showCheck = !mixedBase;

  // ⛔ TWO CATEGORIES WERE ADDED INTO ONE SUBTOTAL. A running tally accumulated EVERY line's amount, so a Volusia
  // bill printing 8,424.63 ad valorem / 4.80 assessments / 8,429.43 combined showed a single "Σ Total" of
  // 8,424.77 — the ad valorem lines plus the fire assessment, which is neither of the bill's own figures and
  // reconciles against nothing. A flat charge does not scale with value and is levied under its own heading;
  // folding it into an ad valorem total makes the one number a reader would check against the bill wrong.
  //
  // ⛔ NOT A NEW SEPARATION — splitJurisdictionLines already returns sumRatedAmount / sumFixedAmount / total,
  // from classifyLine, the one classifier the scanner and the reports both use. This used to ignore it and
  // keep its own tally.
  //
  // ⛔ WHICH SIDE A LINE FALLS ON IS classifyLine's CALL, NOT THE BILL'S HEADING. A flat charge carrying a
  // taxable_value gets an implied rate recovered from amount ÷ taxable and counts as RATED. That is right far
  // more often than not (it is how a rate lost to a wrapped line is recovered), but it means the split can
  // disagree with the bill's own categories — which check 7 in scripts/check-tax-bills.mjs will surface,
  // because the ad valorem Σ then misses total_ad_valorem.
  const { sumRatedAmount, sumFixedAmount, total: sumAllAmount } = splitJurisdictionLines(list);
  const hasFixed = sumFixedAmount != null && sumFixedAmount !== 0;
  const rows = list.map((r, i) => {
    const rate = num(r.tax_rate), taxable = num(r.taxable_value), amount = num(r.amount);
    // sumRate is accumulated below from the CLASSIFIER, not from num(): num('833-275-7297') is 833, and
    // an LA County direct assessment prints a contact phone number where a rate belongs. `rate` here is
    // only for display. The consolidated figure the TS reads must be the one the scanner writes.
    const zeroLine = (rate == null || rate === 0) && (amount == null || amount === 0);
    const noTaxable = taxable == null || taxable === 0;   // e.g. FOREST — can't verify, stays neutral
    // ⛔ ONE SHARED CHECK. This used to compute its own verdict with max($2, amount × 1%) while
    // DocumentScanner's advReconcile used a flat $2 for the same question — so a line could show ⚠ here and
    // still count as consistent there. Both now call lineReconcile, whose tolerance is DERIVED from the
    // printed rate's precision (see taxRate.js). On a $1,675 line the old rule allowed $16.75 of drift.
    let verdict = 'neutral', expected = null;
    if (showCheck && !zeroLine && !noTaxable) {
      const rec = lineReconcile(r, divisor);
      if (rec.ok !== null) { expected = rec.expected; verdict = rec.ok ? 'ok' : 'warn'; }
    }
    return { i, name: r.name || `Line ${i + 1}`, rate, taxable, amount, verdict, expected };
  });
  const ratedSum = sumRatedRate(list);
  const sumRateShown = ratedSum == null ? 0 : ratedSum;
  const consolidatedPct = unit === 'mills' ? sumRateShown / 10 : sumRateShown;   // 68.7 mills → 6.87%

  const th = { padding: '7px 10px', fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: c.muted, textAlign: 'left', background: c.th, borderBottom: `1px solid ${c.border}`, whiteSpace: 'nowrap' };
  const rth = { ...th, textAlign: 'right' };
  const td = { padding: '7px 10px', fontSize: '12px', color: c.text, borderBottom: `1px solid ${c.line}` };
  const rtd = { ...td, textAlign: 'right', fontFamily: 'monospace' };
  // Edit inputs sit INSIDE the cell and inherit its alignment, so an editable table has the same column
  // geometry as a read-only one — the TS is correcting a transcription, not filling in a form.
  const inp = (align) => ({ width: '100%', boxSizing: 'border-box', padding: '3px 5px', fontSize: '12px',
    fontFamily: align === 'right' ? 'monospace' : 'inherit', textAlign: align, color: c.text,
    background: 'transparent', border: `1px solid ${c.line}`, borderRadius: '4px', outline: 'none' });
  const editCell = (i, field, value, align) => (
    <input style={inp(align)} value={value == null ? '' : String(value)}
      onChange={e => onChange(i, field, e.target.value)} />
  );

  if (!list.length) return <div style={{ padding: '10px 12px', fontSize: '12px', color: c.muted, border: `1px solid ${c.border}`, borderRadius: '8px', marginTop: '8px' }}>No jurisdiction lines on this bill.</div>;

  return (
    <div style={{ border: `1px solid ${c.border}`, borderRadius: '8px', overflow: 'hidden', marginTop: '8px' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead><tr>
          <th style={th}>Jurisdiction</th>
          <th style={rth}>{unitLabel}</th>
          <th style={rth}>Taxable Value</th>
          <th style={rth}>Amount</th>
          {showCheck && <th style={{ ...th, textAlign: 'center' }}>✓</th>}
          {removable && <th style={th} />}
        </tr></thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.i}>
              <td style={td}>{editable ? editCell(r.i, 'name', list[r.i]?.name, 'left') : r.name}</td>
              <td style={rtd}>{editable ? editCell(r.i, 'tax_rate', list[r.i]?.tax_rate, 'right') : rateStr(r.rate)}</td>
              <td style={rtd}>{editable ? editCell(r.i, 'taxable_value', list[r.i]?.taxable_value, 'right') : money(r.taxable)}</td>
              <td style={rtd}>
                {editable ? editCell(r.i, 'amount', list[r.i]?.amount, 'right') : money(r.amount)}
                {/* ⛔ NOT "expected $X" — that figure is rate x taxable, and the rate may be the misread half.
                    Offering it invites the TS to type it in and turn the ⚠ green on a line that is still wrong. */}
                {r.verdict === 'warn' && <div style={{ fontSize: '10px', color: 'var(--red)', fontWeight: 600 }}>check rate &amp; amount</div>}
              </td>
              {showCheck && <td style={{ ...td, textAlign: 'center' }}>
                {r.verdict === 'ok' ? <span style={{ color: '#34d399' }}>✓</span>
                  : r.verdict === 'warn' ? <span style={{ color: 'var(--red)' }}>⚠</span>
                  : <span style={{ color: c.muted }}>—</span>}
              </td>}
              {removable && <td style={{ ...td, textAlign: 'center', width: '24px' }}>
                <button type="button" onClick={() => onRemove(r.i)} title="Remove this line"
                  style={{ background: 'none', border: 'none', color: c.muted, cursor: 'pointer', fontSize: '13px', padding: 0 }}>✕</button>
              </td>}
            </tr>
          ))}
        </tbody>
        <tfoot>
          {/* The bill's own three figures, in the bill's own order: ad valorem, assessments, combined. The
              assessments and combined rows appear only when there IS a flat line — on a bill of pure ad
              valorem a second row reading the same number twice would be noise. */}
          <tr style={{ background: c.foot }}>
            <td style={{ ...td, fontWeight: 700 }}>Σ Ad Valorem</td>
            <td style={{ ...rtd, fontWeight: 700 }}>{mixedBase ? '—' : rateStr(sumRateShown)}</td>
            <td style={rtd} />
            <td style={{ ...rtd, fontWeight: 700 }}>{money(sumRatedAmount)}</td>
            {showCheck && <td style={td} />}
            {removable && <td style={td} />}
          </tr>
          {hasFixed && (
            <tr style={{ background: c.foot }}>
              <td style={{ ...td, color: c.sub }}>Assessments <span style={{ color: c.muted, fontWeight: 400 }}>(not rate-based)</span></td>
              <td style={rtd} />
              <td style={rtd} />
              <td style={{ ...rtd, color: c.sub }}>{money(sumFixedAmount)}</td>
              {showCheck && <td style={td} />}
              {removable && <td style={td} />}
            </tr>
          )}
          {hasFixed && (
            <tr style={{ background: c.foot }}>
              <td style={{ ...td, fontWeight: 700 }}>Σ Total</td>
              <td style={rtd} />
              <td style={rtd} />
              <td style={{ ...rtd, fontWeight: 700 }}>{money(sumAllAmount)}</td>
              {showCheck && <td style={td} />}
              {removable && <td style={td} />}
            </tr>
          )}
          <tr style={{ background: c.foot }}>
            <td colSpan={4 + (showCheck ? 1 : 0) + (removable ? 1 : 0)} style={{ padding: '8px 10px', fontSize: '11px', color: c.sub, borderTop: `1px solid ${c.border}` }}>
              {mixedBase
                ? <span style={{ color: c.muted }}>Multi-jurisdiction — authorities tax different bases, so per-line reconciliation and a single consolidated rate aren't computed. Lines shown as transcribed from the bill; the billed amount is authoritative.</span>
                : unit === 'mills'
                  ? <>Consolidated rate: <b>Σ {rateStr(sumRateShown)} mills → {consolidatedPct.toLocaleString('en-US', { maximumFractionDigits: 4 })}%</b> <span style={{ color: c.muted }}>(mills ÷ 10)</span></>
                  : <>Consolidated rate: <b>Σ {consolidatedPct.toLocaleString('en-US', { maximumFractionDigits: 6 })}%</b></>}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
